// Deny-by-default permission broker (decisions D4). A plugin holds NO capability
// until the user consents. Capabilities not declared in the manifest can never
// be granted. Grants persist (via the injected store) and are revocable, and
// every change is broadcast so a running plugin can react live.

export type Capability = 'fs.read' | 'fs.write' | 'pty' | 'net' | 'clipboard' | 'git';
export type PermissionState = 'granted' | 'denied' | 'prompt';

/** Persisted grant flags per plugin (e.g. localStorage-backed in the app). */
export interface GrantStore {
  get(pluginId: string): Record<string, boolean> | undefined;
  set(pluginId: string, grants: Record<string, boolean>): void;
}

/** Host-rendered, unspoofable consent. Returns true iff the user grants. */
export interface ConsentPrompt {
  request(pluginId: string, capability: Capability, reason: string): Promise<boolean>;
}

type ChangeListener = (pluginId: string, capability: Capability, state: PermissionState) => void;

export class PermissionBroker {
  private readonly declared = new Map<string, Set<Capability>>();
  private readonly listeners = new Set<ChangeListener>();

  constructor(
    private readonly store: GrantStore,
    private readonly consent: ConsentPrompt,
  ) {}

  /** Record what a plugin's manifest declared (only these can ever be granted). */
  declare(pluginId: string, capabilities: Capability[]): void {
    this.declared.set(pluginId, new Set(capabilities));
  }

  query(pluginId: string, capability: Capability): PermissionState {
    if (this.isGranted(pluginId, capability)) return 'granted';
    if (this.declared.get(pluginId)?.has(capability)) return 'prompt';
    return 'denied';
  }

  /** True only when currently granted. The broker checks this before every
   *  brokered operation. */
  check(pluginId: string, capability: Capability): boolean {
    return this.query(pluginId, capability) === 'granted';
  }

  /** Ensure a capability is granted, prompting once if needed. Undeclared
   *  capabilities are refused without ever prompting. */
  async request(pluginId: string, capability: Capability, reason: string): Promise<boolean> {
    const state = this.query(pluginId, capability);
    if (state === 'granted') return true;
    if (state === 'denied') return false;
    const ok = await this.consent.request(pluginId, capability, reason);
    if (ok) this.grant(pluginId, capability);
    return ok;
  }

  grant(pluginId: string, capability: Capability): void {
    if (!this.declared.get(pluginId)?.has(capability)) return;
    this.store.set(pluginId, { ...this.grants(pluginId), [capability]: true });
    this.emit(pluginId, capability, 'granted');
  }

  revoke(pluginId: string, capability: Capability): void {
    const next = { ...this.grants(pluginId) };
    delete next[capability];
    this.store.set(pluginId, next);
    // Broadcast 'denied' so running plugins and setup.ts listeners know access
    // is now blocked. query() would return 'prompt' (declared but not granted),
    // which is the wrong semantic for an access-revocation event.
    this.emit(pluginId, capability, 'denied');
  }

  onDidChange(cb: ChangeListener): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  private grants(pluginId: string): Record<string, boolean> {
    return this.store.get(pluginId) ?? {};
  }

  private isGranted(pluginId: string, capability: Capability): boolean {
    return this.grants(pluginId)[capability] === true;
  }

  private emit(pluginId: string, capability: Capability, state: PermissionState): void {
    for (const cb of [...this.listeners]) cb(pluginId, capability, state);
  }
}
